import type { RouteLocationNormalized, RouteLocationRaw, RouteParams, RouteParamsRawGeneric } from "vue-router";

import { Schulform } from "@core/asd/types/schule/Schulform";
import { DeveloperNotificationException } from "@core/core/exceptions/DeveloperNotificationException";
import { BenutzerKompetenz } from "@core/core/types/benutzer/BenutzerKompetenz";
import { ServerMode } from "@core/core/types/ServerMode";
import type { TabData } from "@ui/ui/nav/TabData";

import type { SchuelerLernabschnitteProps } from "~/components/schueler/lernabschnitte/SchuelerLernabschnitteProps";
import { RouteDataSchuelerLernabschnitte } from "~/router/apps/schueler/lernabschnitte/RouteDataSchuelerLernabschnitte";
import { routeSchuelerLernabschnittAllgemein } from "~/router/apps/schueler/lernabschnitte/RouteSchuelerLernabschnittAllgemein";
import { routeSchuelerLernabschnittKonferenz } from "~/router/apps/schueler/lernabschnitte/RouteSchuelerLernabschnittKonferenz";
import { routeSchuelerLernabschnittLeistungen } from "~/router/apps/schueler/lernabschnitte/RouteSchuelerLernabschnittLeistungen";
import { routeSchuelerLernabschnittNachpruefung } from "~/router/apps/schueler/lernabschnitte/RouteSchuelerLernabschnittNachpruefung";
import { routeSchuelerLernabschnittVersetzungAbschluss } from "~/router/apps/schueler/lernabschnitte/RouteSchuelerLernabschnittVersetzungAbschluss";
import { routeSchuelerLernabschnittZeugnisdruck } from "~/router/apps/schueler/lernabschnitte/RouteSchuelerLernabschnittZeugnisdruck";
import { type RouteSchueler } from "~/router/apps/schueler/RouteSchueler";
import { routeError } from "~/router/error/RouteError";
import { RouteManager } from "~/router/RouteManager";
import { RouteNode } from "~/router/RouteNode";

import { routeSchuelerLernabschnittFoerderempfehlungen } from "./RouteSchuelerLernabschnittFoerderempfehlungen";
import { routeSchuelerLernabschnittGostKlausuren } from "./RouteSchuelerLernabschnittGostKlausuren";

const SchuelerLernabschnitte = () => import("~/components/schueler/lernabschnitte/SchuelerLernabschnitte.vue");


export class RouteSchuelerLernabschnitte extends RouteNode<RouteDataSchuelerLernabschnitte, RouteSchueler> {

	public constructor() {
		super(Schulform.values(), [BenutzerKompetenz.SCHUELER_LEISTUNGSDATEN_ANSEHEN], "schueler.lernabschnitt", String.raw`lernabschnitt/:abschnitt(\d+)?/:wechselNr(\d+)?`, SchuelerLernabschnitte, new RouteDataSchuelerLernabschnitte());
		super.mode = ServerMode.STABLE;
		super.propHandler = (route) => this.getProps(route);
		super.text = "Lernabschnitte";
		super.children = [
			routeSchuelerLernabschnittAllgemein,
			routeSchuelerLernabschnittLeistungen,
			routeSchuelerLernabschnittGostKlausuren,
			routeSchuelerLernabschnittVersetzungAbschluss,
			routeSchuelerLernabschnittKonferenz,
			routeSchuelerLernabschnittZeugnisdruck,
			routeSchuelerLernabschnittNachpruefung,
			routeSchuelerLernabschnittFoerderempfehlungen,
		];
		super.defaultChild = routeSchuelerLernabschnittLeistungen;
	}

	protected async update(to: RouteNode<any, any>, to_params: RouteParams, from: RouteNode<any, any> | undefined, from_params: RouteParams, isEntering: boolean): Promise<void | Error | RouteLocationRaw> {
		try {
			const { id, abschnitt: idSchuljahresabschnitt, wechselNr } = RouteNode.getIntParams(to_params, ["id", "abschnitt", "wechselNr"]);
			if (id === undefined) {
				throw new DeveloperNotificationException("Fehler: Keine Schüler-ID in der URL angegeben.");
			}
			await this.data.setSchueler(id, isEntering);
			if (idSchuljahresabschnitt !== undefined) {
				await routeSchuelerLernabschnitte.data.setLernabschnitt(idSchuljahresabschnitt, wechselNr ?? 0);
			}
			if ((to === this) && (this.data.hatAuswahl)) {
				return this.getRouteView(this.data.view);
			}
			if (!to.name.startsWith(this.data.view.name)) {
				for (const child of this.children) {
					if (to.name.startsWith(child.name)) {
						this.data.setView(child, this.children);
					}
				}
			}
		} catch (e) {
			return await routeError.getErrorRoute(e as DeveloperNotificationException);
		}
	}

	public async leave(from: RouteNode<any, any>, from_params: RouteParams): Promise<void> {
		this.data.reset();
	}

	public addRouteParamsFromState(): RouteParamsRawGeneric {
		const abschnitt = this.data.hatAuswahl ? this.data.auswahl.schuljahresabschnitt : undefined;
		const wechselNr = this.data.hatAuswahl ? this.data.auswahl.wechselNr : undefined;
		return { abschnitt, wechselNr };
	}

	public getProps(to: RouteLocationNormalized): SchuelerLernabschnitteProps {
		return {
			lernabschnitt: routeSchuelerLernabschnitte.data.auswahl,
			lernabschnitte: routeSchuelerLernabschnitte.data.listAbschnitte,
			gotoLernabschnitt: routeSchuelerLernabschnitte.data.gotoLernabschnitt,
			tabManager: () => this.createTabManagerByChildren(this.data.view.name, this.setTab),
		};
	}

	private setTab = async (value: TabData) => {
		if (value.name === this.data.view.name) {
			return;
		}
		const node = RouteNode.getNodeByName(value.name);
		if (node === undefined) {
			throw new DeveloperNotificationException("Unbekannte Route");
		}
		await RouteManager.doRoute(this.getRouteView(node));
		this.data.setView(node, this.children);
	};

}

export const routeSchuelerLernabschnitte = new RouteSchuelerLernabschnitte();
